import type { IConversationMessage } from '../hooks/useAssistantWebSocket';
import type { SnapshotMessage } from '@orn/global/assistant/snapshotMessage.js';

export type { SnapshotMessage };

/**
 * Build a fresh `IConversationMessage[]` from a server `transcript_snapshot`.
 * Tool-completed snapshot rows are not rendered or logged on rehydration
 * (tool detail is a live-only debug console concern), so they are skipped.
 *
 * Snapshot-derived messages are never pending (`isPending` omitted) and
 * carry `clientMessageId` directly off the wire row (`undefined` when the
 * server didn't populate one, e.g. eval-path rows).
 *
 * `generateId` is injected so callers can stub it in tests and the hook can
 * pass its own time+random implementation in production.
 */
export function snapshotToConversation(snapshot: SnapshotMessage[], generateId: () => string): IConversationMessage[] {
  const out: IConversationMessage[] = [];
  for (const row of snapshot) {
    switch (row.kind) {
      case 'user':
        out.push({
          id: `msg-${generateId()}`,
          role: 'user',
          content: row.content,
          timestamp: new Date(row.timestamp),
          clientMessageId: row.clientMessageId,
          ...(row.attachments && row.attachments.length > 0 ? { attachments: row.attachments } : {}),
        });
        break;
      case 'assistant':
        out.push({
          id: `msg-${generateId()}`,
          role: 'assistant',
          content: row.content,
          timestamp: new Date(row.timestamp),
        });
        break;
      case 'tool_completed':
        // Tool detail is not surfaced on rehydration — skip.
        break;
      default: {
        // Compile-time exhaustiveness check: adding a new `SnapshotMessage`
        // variant without a case branch fails here.
        const _exhaustive: never = row;
        void _exhaustive;
      }
    }
  }
  return out;
}

/**
 * Reconcile prior in-memory messages against a server-authoritative
 * `transcript_snapshot`: replace the list with the snapshot, then re-append
 * any pending user bubble (`isPending: true`) whose `clientMessageId` does
 * not appear on a `snapshot.user` row.
 *
 * Identity match by `clientMessageId` is the only confirmation path —
 * production web clients always populate it, and the snapshot reconciler
 * is only ever exercised against real server snapshots.
 *
 * Non-pending bubbles defer entirely to the snapshot — the server is
 * authoritative for already-confirmed history. The caller passes the raw
 * `SnapshotMessage[]` alongside the converted list so this reconciler can
 * read `clientMessageId` directly off the wire row without re-deriving.
 */
export function reconcileSnapshotMessages(
  prev: IConversationMessage[],
  snapshotMessages: IConversationMessage[],
  snapshotRaw: SnapshotMessage[]
): IConversationMessage[] {
  const seenIds = new Set<string>();
  for (const row of snapshotRaw) {
    if (row.kind === 'user' && row.clientMessageId !== undefined) {
      seenIds.add(row.clientMessageId);
    }
  }
  const preserved = prev.filter(
    (m) => m.isPending === true && (m.clientMessageId === undefined || !seenIds.has(m.clientMessageId))
  );
  return [...snapshotMessages, ...preserved];
}

/**
 * Confirm every pending bubble whose `clientMessageId` appears in
 * `clientMessageIds`. Used when the server replies to a turn — the
 * `assistant_message` echoes the full id array (one for single-message
 * turns, N for batched turns) and all matching bubbles flip from pending
 * to confirmed in a single pass.
 *
 * Returns a NEW array. Bubbles whose ids are not in the set, and non-user /
 * already-confirmed bubbles, are left untouched. Empty `clientMessageIds`
 * (eval-path turn without ids) returns the input unchanged.
 */
export function confirmPendingMessages(
  messages: IConversationMessage[],
  clientMessageIds: string[]
): IConversationMessage[] {
  if (clientMessageIds.length === 0) {
    return messages;
  }
  const idSet = new Set(clientMessageIds);
  let changed = false;
  const next = messages.map((m) => {
    if (m.role === 'user' && m.isPending === true && m.clientMessageId !== undefined && idSet.has(m.clientMessageId)) {
      changed = true;
      return { ...m, isPending: false };
    }
    return m;
  });
  return changed ? next : messages;
}

/**
 * Insert an assistant reply into the running transcript at the correct
 * slot. The stream-from-the-server protocol emits N `assistant_message`
 * frames per turn (one per LLM round whose assistant message carried any
 * prose). Insertion priority — first match wins:
 *
 * 1. **Continuation of the current turn** (`previousAssistantId` matches
 *    a bubble): insert immediately AFTER that bubble. Round 2's reply
 *    stacks below round 1's; round 3's below round 2's. Stable across
 *    mid-turn user typing because the anchor is identity-based.
 *
 * 2. **First reply of a user-initiated turn** (no previous, but at least
 *    one `isPending && isInFlight` user bubble exists): insert AFTER the
 *    last such bubble. When the user typed follow-ups mid-turn, those
 *    queued bubbles carry `isInFlight: false`; appending blindly would
 *    slot the reply below them and break the visible thread
 *    (A → B → C → replyA instead of A → replyA → B → C).
 *
 * 3. **First reply of a server-initiated turn** (no previous, no
 *    in-flight pending — today this fires only for the proactive session
 *    bootstrap, and only when the user has queued bubbles mid-turn):
 *    insert BEFORE the first still-pending user bubble. Those queued
 *    bubbles were typed AFTER this server turn started, so the reply
 *    belongs above them chronologically.
 *
 * 4. **Otherwise** (empty transcript, or no pending bubbles at all):
 *    append at the end.
 *
 * Returns a NEW array. Pure function — no React, no refs, fully
 * unit-testable.
 */
export function insertAssistantReply(
  messages: IConversationMessage[],
  reply: IConversationMessage,
  options: { previousAssistantId: string | null }
): IConversationMessage[] {
  const { previousAssistantId } = options;
  let insertIndex: number;
  if (previousAssistantId !== null) {
    const prevIdx = messages.findIndex((m) => m.id === previousAssistantId);
    if (prevIdx !== -1) {
      insertIndex = prevIdx + 1;
    } else {
      // Defensive: the bubble we stored an id for is gone (e.g. a
      // snapshot replaced the transcript mid-turn). Fall through to
      // the pending-anchor heuristics.
      insertIndex = pickPendingAnchorIndex(messages);
    }
  } else {
    insertIndex = pickPendingAnchorIndex(messages);
  }
  return [...messages.slice(0, insertIndex), reply, ...messages.slice(insertIndex)];
}

function pickPendingAnchorIndex(messages: IConversationMessage[]): number {
  const lastInFlight = messages.findLastIndex((m) => m.isPending === true && m.isInFlight === true);
  if (lastInFlight !== -1) {
    return lastInFlight + 1;
  }
  const firstPending = messages.findIndex((m) => m.isPending === true);
  return firstPending === -1 ? messages.length : firstPending;
}

// ============================================
// Streaming-bubble lifecycle
// ============================================
//
// Token-level streaming grows one assistant bubble in place across a round's
// `assistant_message_delta` frames, then snaps it to the authoritative text on
// the terminal `assistant_message`. The bubble is seeded by `insertAssistantReply`
// (above) carrying `isStreaming: true`, grown by `appendStreamingDelta`, and then
// either `finalizeStreamingBubble` (success) or `dropStreamingBubble` (error /
// abort mid-stream). All three target the bubble by id, return a NEW array, and
// preserve the object identity of every untouched row so a memoized row list
// re-renders only the bubble that changed.

/**
 * Append a token-level delta to the streaming bubble, concatenating onto its
 * running text. A no-op (input contents unchanged) when no row matches —
 * defensive against a stray delta after the bubble was dropped.
 */
export function appendStreamingDelta(
  messages: IConversationMessage[],
  streamingId: string,
  text: string
): IConversationMessage[] {
  return messages.map((m) => (m.id === streamingId ? { ...m, content: m.content + text } : m));
}

/**
 * Snap the streaming bubble to the round's authoritative full text and clear its
 * `isStreaming` flag. Replacing (not appending) the content drops any client-side
 * accumulation drift between the streamed deltas and the server's canonical text.
 */
export function finalizeStreamingBubble(
  messages: IConversationMessage[],
  streamingId: string,
  content: string
): IConversationMessage[] {
  return messages.map((m) => {
    if (m.id !== streamingId) {
      return m;
    }
    const finalized = { ...m, content };
    delete finalized.isStreaming;
    return finalized;
  });
}

/**
 * Remove an unfinalized streaming bubble (error / abort mid-round). Tolerant of a
 * missing id (returns an equivalent list).
 */
export function dropStreamingBubble(messages: IConversationMessage[], streamingId: string): IConversationMessage[] {
  return messages.filter((m) => m.id !== streamingId);
}
